// Auth store — holds the currently signed-in Google user.
//
// Persisted to localStorage so reloads don't punt the user back to
// the gate. JWT expiry is honored; on load, if exp has passed we
// drop the saved user and the gate re-renders. No refresh-token
// dance — GIS doesn't give us one in the browser-only flow, so
// re-auth is a single click on the Google button.

import { create } from "zustand";
import type { GoogleUser } from "../lib/google-auth";

const STORAGE_KEY = "agicore-suite/auth/v1";

interface PersistedUser extends GoogleUser {
  saved_at: number;
}

function loadInitial(): GoogleUser | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedUser;
    // JWT exp is unix-seconds; bail early if expired.
    const nowSec = Math.floor(Date.now() / 1000);
    if (!parsed.exp || parsed.exp <= nowSec) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const { saved_at: _saved, ...user } = parsed;
    void _saved;
    return user;
  } catch (e) {
    console.warn("[auth] failed to load persisted user:", e);
    return null;
  }
}

interface AuthState {
  user: GoogleUser | null;
  /** True once initializeGoogleAuth has resolved. The gate uses this
   *  to decide whether to render the Google button or a "loading"
   *  message — avoids a flash of empty button container. */
  ready: boolean;
  setUser: (user: GoogleUser | null) => void;
  setReady: (ready: boolean) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: loadInitial(),
  ready: false,
  setUser: (user) => {
    set({ user });
    if (typeof localStorage !== "undefined") {
      try {
        if (user) {
          const persisted: PersistedUser = { ...user, saved_at: Date.now() };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.warn("[auth] persist failed:", e);
      }
    }
  },
  setReady: (ready) => set({ ready }),
  signOut: () => {
    set({ user: null });
    if (typeof localStorage !== "undefined") {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
  },
}));
